import voca from 'voca';
import { getLogger } from '@cld/upload-widget';
import createBoundActionCreators, { createCustomCreator } from 'common/actionsBase';
import { selectCloudName, selectAutoMinimizeConfig } from 'root/selectors';
import { addNotification } from 'root/actions';
import notifications, { NOTIFICATION_IDS } from 'root/data/NotificationsData';
import getShutterstock from '../service';
import {
  selectAuthData,
  selectSearchParams,
  selectMediaType,
  selectPage,
  selectPurchaseDetails,
  selectSelectedItems,
  selectFormat,
  selectDefaultSubscriptionId,
  selectSizeParams,
  selectMetadataSchema,
  selectPurchaseAssets,
  selectHasSearchParams,
  selectDefaultSubscriptionPrice,
} from '../selectors';
import consts from '../data/consts';

const logger = getLogger();

export const types = {
  GET_RESULTS: 'SHUTTER_GET_RESULTS',
  GET_CATEGORIES: 'SHUTTER_GET_CATEGORIES',
  SET_AUTH_INFO: 'SHUTTER_SET_AUTH_INFO',
  SET_RESULTS_RETRIEVE_START: 'SHUTTER_SET_RESULTS_RETRIEVE_START',
  SET_RESULTS_RETRIEVE_SUCCESS: 'SHUTTER_SET_RESULTS_RETRIEVE_SUCCESS',
  SET_ACCOUNT_INFO_REQUESTED: 'SHUTTER_SET_ACCOUNT_INFO_REQUESTED',
  SET_ACCOUNT_INFO_SUCCESS: 'SHUTTER_SET_ACCOUNT_INFO_SUCCESS',
  SET_USER_LOGOUT_SUCCESS: 'SHUTTER_SET_USER_LOGOUT_SUCCESS',
  SET_SEARCH_PARAMS: 'SHUTTER_SET_SEARCH_PARAMS',
  SET_SEARCH_TERM_TEXT: 'SHUTTER_SET_SEARCH_TERM_TEXT',
  SET_PURCHASE_START: 'SHUTTER_SET_PURCHASE_START',
  SET_PURCHASE_DETAILS: 'SHUTTER_SET_PURCHASE_DETAILS',
  SET_PURCHASE_IN_PROGRESS: 'SHUTTER_SET_PURCHASE_IN_PROGRESS',
  SET_CATEGORIES: 'SHUTTER_SET_CATEGORIES',
  SET_ASSET_PURCHASE_DETAILS: 'SHUTTER_SET_ASSET_PURCHASE_DETAILS',
  SET_SELECTION_FOR_CONTEXT: 'SHUTTER_SET_SELECTION_FOR_CONTEXT',
  SET_PURCHASE_SUCCESS: 'SHUTTER_SET_PURCHASE_SUCCESS',
  CLEAR_RESULTS_FOR_CONTEXT: 'SHUTTER_CLEAR_RESULTS_FOR_CONTEXT',
  CLEAR_PURCHASE_DETAILS: 'SHUTTER_CLEAR_PURCHASE_DETAILS',
  CLEAR_SELECTION_FOR_CONTEXT: 'SHUTTER_CLEAR_SELECTION_FOR_CONTEXT',
  CLEAR_SEARCH_TERM: 'SHUTTER_CLEAR_SEARCH_TERM',
  CLEAR_SEARCH_FILTERS: 'SHUTTER_CLEAR_SEARCH_FILTERS',
  RETRIEVE_ASSETS: 'SHUTTER_RETRIEVE_ASSETS',
  RETRIEVE_MORE_ASSETS: 'SHUTTER_RETRIEVE_MORE_ASSETS',
  RESET_MAX_RESULTS: 'SHUTTER_RESET_MAX_RESULTS',
  DO_LOGOUT: 'SHUTTER_DO_LOGOUT',
  TOGGLE_FILTERS_PANEL: 'SHUTTER_TOGGLE_FILTERS_PANEL',
  DOWNLOAD: 'SHUTTER_DOWNLOAD',
  PURCHASE: 'SHUTTER_PURCHASE',
  INIT_AUTH: 'SHUTTER_INIT_AUTH',
};

const getCurrentAccount = async (authInfo, dispatch, dispatcher) => {
  logger.log('[shutterstockTab.actions]: about to retrieve account info');

  try {
    dispatcher(types.SET_ACCOUNT_INFO_REQUESTED);
    const account = await getShutterstock().getAccountInfo(authInfo);
    logger.log('[shutterstockTab.actions]: retrieved account info: ', account);
    dispatcher(types.SET_ACCOUNT_INFO_SUCCESS, { account });
  } catch (ex) {
    logger.log('[shutterstockTab.actions]: failed to get account info', ex);
    logOutOnAccessDenied({ status: 403 }, authInfo.cloudName, dispatch, dispatcher); // log out, no point continuing without account details
  }
};

const setAuthInfo = async (authInfo, dispatch, getState, dispatcher) => {
  dispatcher(types.SET_AUTH_INFO, authInfo);

  if (authInfo && authInfo.token) {
    getShutterstock().getTtlHandler().startTimer();
    await getCurrentAccount(authInfo, dispatch, dispatcher);
  }
};

const logOut = (cloudName, dispatcher) => {
  getShutterstock().logout(cloudName);
  dispatcher(types.SET_USER_LOGOUT_SUCCESS);
  getShutterstock().getTtlHandler().clearTimer();
};

const logOutOnAccessDenied = (err, cloudName, dispatch, dispatcher) => {
  const tokenTimer = getShutterstock().getTtlHandler().getTtlInMinutes();
  if (err.status === 401 || err.status === 403 || tokenTimer <= 0) {
    logger.log('[shutterstockTab.actions]: logging out user on api auth error', err);
    logOut(cloudName, dispatcher);

    addNotification(notifications[NOTIFICATION_IDS.SERVICE_LOGGED_OUT], dispatch);
  }
};

const retrieveAssets = async (data, dispatch, getState, dispatcher) => {
  const state = getState();
  const authData = selectAuthData(state);

  data = data || { page: 1 };

  if (authData.isAuth) {
    const service = getShutterstock();
    const context = consts.CONTEXTS.SEARCH; // ok to use (instead of selector) because currently only one context
    const cloudName = selectCloudName(state);
    const params = selectSearchParams(state);
    const nextPage = data.page;

    const inProgressActions = [[types.SET_RESULTS_RETRIEVE_START, { context }]];

    if (data.page <= 1) {
      // clear previous results on first page
      inProgressActions.push([types.CLEAR_RESULTS_FOR_CONTEXT, { context }]);
    }

    dispatcher(inProgressActions);

    let response = null;

    try {
      response = await service.getSearchResults(cloudName, params, nextPage);

      if (!response.results.length && params.searchTerm && params.mediaType) {
        // as a fallback try to find a specific image by id
        response = await service.getImageById(cloudName, params.mediaType, params.searchTerm);
      }
    } catch (ex) {
      logger.log('[shutterstockTab.actions]: encountered error when trying to retrieve context assets', ex);
      logOutOnAccessDenied(ex, cloudName, dispatch, dispatcher);
    }

    if (response) {
      logger.log(`[shutterstockTab.actions]: finished retrieving assets for context: ${context}`);
      dispatcher(types.SET_RESULTS_RETRIEVE_SUCCESS, { context, response });
    }
  }
};

const isChangedFilter = (dataFilters, stateFilters) => !!Object.entries(dataFilters).find(([key, value]) => stateFilters[key] !== value);

const SEARCH_PARAM_COMPARERS = ['maxResults', 'sort', 'searchTerm', 'mediaType', ['filters', isChangedFilter]];

const didSearchParamsChange = (data, stateParams) =>
  !!SEARCH_PARAM_COMPARERS.find((type) => {
    const checker = voca.isString(type) ? { key: type } : { key: type[0], fn: type[1] };

    return checker.fn
      ? checker.fn(data[checker.key], stateParams[checker.key]) // use custom checker
      : data[checker.key] !== stateParams[checker.key]; // use simple equality compare
  });

export const getDefaultSize = (asset, state) => {
  const assetKeys = Object.keys(asset.assets);
  const sizeParams = selectSizeParams(state);

  const matched = consts.DEFAULT_SIZE_ORDER.find((size) => {
    const sizeType = consts.SIZE_TYPES[size];

    return (
      assetKeys.includes(size) &&
      sizeParams.allowedSizes.includes(sizeType.name || size) &&
      (!sizeType.type || asset.assets[size].file_size <= (sizeType.type === consts.MEDIA_TYPES.IMAGES ? sizeParams.maxImageSize : sizeParams.maxVideoSize))
    );
  });

  return matched ? consts.SIZE_TYPES[matched].name || matched : null;
};

export const getMetadata = (state) => {
  let metadata = {};
  const metadataSchema = selectMetadataSchema(state);

  if (metadataSchema) {
    const purchaseAssets = selectPurchaseAssets(state);

    metadataSchema.forEach((schemaItem) => (metadata[schemaItem.key] = ''));

    const licenceInfo = purchaseAssets && purchaseAssets[0] && purchaseAssets[0].licenceInfo;

    if (licenceInfo && licenceInfo.metadata) {
      metadata = {
        ...metadata,
        ...licenceInfo.metadata,
      };
    }
  }

  return metadata;
};

const trimValues = (value) => {
  let trimmedValue = typeof value === 'string' ? value.trim() : value;

  if (typeof value === 'object') {
    const draft = Array.isArray(value) ? [...value] : { ...value };
    Object.entries(draft).forEach(([key, value]) => (draft[key] = trimValues(value)));

    trimmedValue = draft;
  }

  return trimmedValue;
};

const getClearAction = (clearType) =>
  createCustomCreator((dispatch, getState, dispatcher) => {
    dispatcher(clearType);

    if (!selectHasSearchParams(getState())) {
      // when there arent any search parameters anymore then we only need results for the selection page (consts.MAX_RESULTS.SELECTION)
      dispatcher(types.RESET_MAX_RESULTS);
    }

    return retrieveAssets(null, dispatch, getState, dispatcher); // fetch again without the cleared filters
  });

export default createBoundActionCreators(types, {
  [types.INIT_AUTH]: createCustomCreator(async (cloudName, dispatch, getState, dispatcher) => {
    dispatcher(types.INIT_AUTH);
    await setAuthInfo(getShutterstock().getAuthInfo(cloudName), dispatch, getState, dispatcher);
  }),

  [types.SET_AUTH_INFO]: createCustomCreator(setAuthInfo),

  [types.SET_SEARCH_PARAMS]: createCustomCreator((data, dispatch, getState, dispatcher) => {
    const state = getState();
    const stateParams = selectSearchParams(state);

    if (didSearchParamsChange(data, stateParams)) {
      // check if action actually changes the search criteria
      dispatcher(types.SET_SEARCH_PARAMS, data); // make sure to update search params first before retrieving assets
      retrieveAssets(null, dispatch, getState, dispatcher);
    }
  }),

  [types.RETRIEVE_ASSETS]: createCustomCreator(retrieveAssets),

  [types.RETRIEVE_MORE_ASSETS]: createCustomCreator((dispatch, getState, dispatcher) => {
    const page = selectPage(getState()) + 1;
    return retrieveAssets({ page }, dispatch, getState, dispatcher);
  }),

  [types.CLEAR_SEARCH_TERM]: getClearAction(types.CLEAR_SEARCH_TERM),

  [types.CLEAR_SEARCH_FILTERS]: getClearAction(types.CLEAR_SEARCH_FILTERS),

  [types.GET_CATEGORIES]: createCustomCreator(async (dispatch, getState, dispatcher) => {
    let response = null;

    const state = getState();
    const authData = selectAuthData(state);

    if (authData.isAuth) {
      const cloudName = selectCloudName(state);

      try {
        response = await getShutterstock().getCategories(cloudName);
      } catch (ex) {
        logger.log('[shutterstockTab.actions]: encountered error when trying to retrieve categories', ex);
        logOutOnAccessDenied(ex, cloudName, dispatch, dispatcher);
      }

      if (response) {
        logger.log('[shutterstockTab.actions]: finished retrieving categories');
        dispatcher(types.SET_CATEGORIES, response);
      }
    }

    return response;
  }),

  /**
   * currently only supports single item.
   * if in the future multiple purchase is supported then will need to refactor
   */
  [types.SET_PURCHASE_START]: createCustomCreator(async (dispatch, getState, dispatcher) => {
    let state = getState();

    const cloudName = selectCloudName(state);
    const mediaType = selectMediaType(state);
    const selectedItems = selectSelectedItems(state);
    const asset = selectedItems[0];
    const subId = selectDefaultSubscriptionId(state);
    const price = selectDefaultSubscriptionPrice(state);

    if (subId) {
      logger.log(`[shutterstockTab.actions]: Getting purchase data. using subscription: ${subId}`);

      dispatcher(types.SET_PURCHASE_START, { plan: subId });

      state = getState(); // get updated state

      dispatcher(types.SET_PURCHASE_DETAILS, {
        size: getDefaultSize(asset, state),
        metadata: getMetadata(state),
        price,
      });

      let result;

      try {
        result = await getShutterstock().retrieveAssetPurchaseData(asset, cloudName, mediaType);
      } catch (ex) {
        logger.log(`[shutterstockTab.actions]: failed to retrieve asset (id: ${selectedItems[0].id} data for purchase `, ex);
        logOutOnAccessDenied(ex, cloudName, dispatch, dispatcher);
      }

      if (result) {
        dispatcher(types.SET_ASSET_PURCHASE_DETAILS, {
          details: [result],
        });
      }
    } else {
      logger.log("[shutterstockTab.actions]: can't proceed with purchase flow because no plan found");
    }
  }),

  [types.SET_PURCHASE_DETAILS]: createCustomCreator((data, dispatch, getState, dispatcher) => {
    let state = getState();

    const purchaseDetails = selectPurchaseDetails(state);
    const selectedItems = selectSelectedItems(state);
    const asset = selectedItems[0];

    if (data.plan && data.plan !== purchaseDetails.plan) {
      logger.log(`[shutterstockTab.actions]: plan changed to ${data.plan}. initializing size and metadata`);

      dispatcher(types.SET_PURCHASE_START, { plan: data.plan });

      state = getState();

      dispatcher(types.SET_PURCHASE_DETAILS, {
        size: getDefaultSize(asset, state),
        metadata: getMetadata(state),
        ...data,
      });
    } else {
      dispatcher(types.SET_PURCHASE_DETAILS, data);
    }
  }),

  [types.PURCHASE]: createCustomCreator(async (dispatch, getState, dispatcher) => {
    dispatcher(types.SET_PURCHASE_IN_PROGRESS, { inProgress: true });

    let response = null;

    const state = getState();
    const cloudName = selectCloudName(state);
    const purchaseDetails = selectPurchaseDetails(state);
    const { id } = selectSelectedItems(state)[0];
    const mediaType = selectMediaType(state);
    const format = selectFormat(state);
    const autoMinimize = selectAutoMinimizeConfig(state);

    const isVideo = mediaType === consts.MEDIA_TYPES.VIDEOS;

    try {
      const idProp = isVideo ? 'video_id' : 'image_id';

      response = await getShutterstock().setAssetsLicence(cloudName, mediaType, [
        {
          [idProp]: id,
          size: purchaseDetails.size,
          format,
          subscription_id: purchaseDetails.plan,
          price: purchaseDetails.price,
          metadata: trimValues(purchaseDetails.metadata),
        },
      ]);
    } catch (ex) {
      logger.log('[shutterstockTab.actions]: encountered error when trying to license asset', ex);
      logOutOnAccessDenied(ex, cloudName, dispatch, dispatcher);
    }

    if (response) {
      if (!response.data[0].error) {
        logger.log('[shutterstockTab.actions]: finished licensing assets');

        dispatcher(types.SET_PURCHASE_SUCCESS, { url: response.data[0].download.url });

        if (!autoMinimize) {
          const notificationId = isVideo ? 'VIDEO_PURCHASED' : 'IMAGE_PURCHASED';
          addNotification(notifications[NOTIFICATION_IDS[notificationId]], dispatch);
        }
      } else {
        logger.log('[shutterstockTab.actions]: purchase failed with error: ', response.data[0].error);
        addNotification(notifications[NOTIFICATION_IDS.PURCHASE_FAILED], dispatch);
      }
    }

    dispatcher(types.SET_PURCHASE_IN_PROGRESS, { inProgress: false });
  }),

  [types.DOWNLOAD]: createCustomCreator(async (dispatch, getState, dispatcher) => {
    dispatcher(types.SET_PURCHASE_IN_PROGRESS, { inProgress: true });

    const state = getState();
    const cloudName = selectCloudName(state);
    const purchaseDetails = selectPurchaseDetails(state);
    const assets = selectPurchaseAssets(state);
    const mediaType = selectMediaType(state);

    let response = null;

    try {
      response = await getShutterstock().download(cloudName, mediaType, purchaseDetails.size, assets[0].licenceInfo.id);
    } catch (ex) {
      logger.log('[shutterstockTab.actions]: encountered error when trying to redownload asset', ex);
      logOutOnAccessDenied(ex, cloudName, dispatch, dispatcher);
    }

    if (response && response.url) {
      logger.log('[shutterstockTab.actions]: finished asset download');
      dispatcher(types.SET_PURCHASE_SUCCESS, { url: response.url });
    } else {
      logger.log('[shutterstockTab.actions]: download failed with error: ', response);
      addNotification(notifications[NOTIFICATION_IDS.PURCHASE_FAILED], dispatch);
    }

    dispatcher(types.SET_PURCHASE_IN_PROGRESS, { inProgress: false });
  }),

  [types.DO_LOGOUT]: createCustomCreator((dispatch, getState, dispatcher) => {
    const cloudName = selectCloudName(getState());
    logger.log('[shutterstockTab.actions]: about to log out shutterstock user');
    logOut(cloudName, dispatcher);
  }),
});
